import {Card} from "../../generic/card";
import {AsRoute} from "../../../app";
import {useFetchAndRender} from "../../fetchAndRender";
import {WithAPI} from "../../../api/temperature";
import {LogEntry, LogOpts, LogResponse, LogSeverity} from "../../../api/admin";
import {useEffect, useRef, useState} from "preact/hooks";
import {DateTime} from "../../generic/date";
import {useTimeout} from "../../../lib/useTimeout";
import {Row, Table} from "../../generic/table";
import styled from "styled-components";

const Json = styled.span`
    white-space: normal;
`;

const JsonItem = styled(Json)`
    display: inline-block;
`;

const JsonValue = styled(Json)`
    display: inline-block;
    vertical-align: middle;
    line-height: 1.2rem;
    padding: 0 .4rem;
`;

const JsonKey = styled(Json)`
    display: inline-block;
    vertical-align: middle;
    background: rgba(255, 255, 255, .1);
    border: solid rgba(0, 0, 0, .4) 1px;
    line-height: 1.2rem;
    padding: 0 .4rem;
`;

const JsonObject = styled(Json)`
    &:before, &:after {
        color: ${props => props.theme.colors.mutedFg};
        vertical-align: middle;
    }
    
    &:before {
        content: '{';
    }
    
    &:after {
        content: '}';
    }
    
    > ${JsonItem} {
        padding-right: .4em;
        display: inline-flex;
        align-items: stretch;
        
        &:last-child {
            padding-right: 0;
        }
        
        > ${JsonKey} {
            border-top-left-radius: .25rem;
            border-bottom-left-radius: .25rem;
            font-size: .6rem;
            border-right: none;
            color: ${props => props.theme.colors.mutedFg};
        }
        
        > ${JsonValue} {
            border-top-right-radius: .25rem;
            border-bottom-right-radius: .25rem;
            
            background: rgba(255, 255, 255, .1);
            border: solid rgba(0, 0, 0, .4) 1px;
        }
    }
    
    &:has(>${JsonItem}:hover):not(:has(* ${JsonItem}:hover))>${JsonItem} {
        background: rgba(255, 0, 0, .6);
        border-radius: .25rem;
    }
`;

const JsonArray = styled(Json)`
    &:before, &:after {
        color: ${props => props.theme.colors.mutedFg};
        vertical-align: middle;
    }
    
    &:before {
        content: '[';
    }
    
    &:after {
        content: ']';
    }
    
    > ${JsonItem} {
        &:after {
            content: ', ';
        }
        
        &:last-child:after {
            content: '';
        }
    }
    
    &:has(>${JsonItem}:hover):not(:has(* ${JsonItem}:hover))>${JsonItem} {
        background: rgba(255, 0, 0, .6);
        
        &:first-child {
            border-top-left-radius: .25rem;
            border-bottom-left-radius: .25rem;
        }
        
        &:last-child {
            border-top-right-radius: .25rem;
            border-bottom-right-radius: .25rem;
        }
    }
`;

const TextPlain = styled.span`
    vertical-align: middle;
    line-height: 1.2rem;
`;

function jsonToHtml(obj: any) {
    if (Array.isArray(obj)) {
        return <JsonArray>{obj.map((item) => <JsonItem>
            <JsonValue>{jsonToHtml(item)}</JsonValue>
        </JsonItem>)}</JsonArray>;
    } else if (typeof obj === "object") {
        return <JsonObject>{Object.keys(obj).map(key => <JsonItem>
            <JsonKey>{key}</JsonKey>
            <JsonValue>{jsonToHtml(obj[key])}</JsonValue>
        </JsonItem>)}</JsonObject>;
    } else {
        return <JsonValue>{obj}</JsonValue>;
    }
}

/**
 * Search for JSON in string, and format it to collapsible elements.
 */
function formatJSON(str: string) {
    let re = /[{\[]{1}([,:{}\[\]0-9.\-+Eaeflnr-u \n\r\t]|".*?")+[}\]]{1}/g;
    let out = [];

    let lastIndex = 0;
    for (const match of str.matchAll(re)) {
        try {
            let replace = jsonToHtml(JSON.parse(match[0]));
            let middle = str.substring(lastIndex, match.index);

            if (middle) {
                out.push(<TextPlain>{middle}</TextPlain>);
            }

            out.push(replace);
            lastIndex = (match.index ?? 0) + match[0].length;
        } catch (e) {
            console.error(e);
        }
    }

    if (lastIndex !== str.length) {
        out.push(<TextPlain>{str.substring(lastIndex)}</TextPlain>);
    }

    return out;
}


export type LogViewProps = {
    severity?: LogSeverity;
    limit?: number;
    interval?: number;
} & WithAPI;

export function LogView(props: LogViewProps) {
    const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
    const logEntriesRef = useRef<LogEntry[] | null>(null);
    const clearLogEntries = useRef<boolean>(false);

    logEntriesRef.current = logEntries;

    const limit = props.limit ?? 20;
    const severity = props.severity ?? "INFO";

    const [result, forceRefresh] = useFetchAndRender<LogResponse, [LogSeverity | undefined, number | undefined]>(() => {
        const opts: LogOpts = {};

        if (logEntriesRef.current) {
            if (logEntriesRef.current.length > 0) {
                opts.since = logEntriesRef.current[0].datetime;
            }
        }

        opts.limit = limit;
        opts.severity = severity;

        return props.api.admin.log(opts);
    }, {interval: props.interval ?? 1}, [props.severity, props.limit]);

    useEffect(() => {
        if (!result) {
            clearLogEntries.current = true;
            return;
        }

        const newLogEntries = Array.from(result.log);

        if (!clearLogEntries.current) {
            newLogEntries.push(...logEntries);
        } else {
            clearLogEntries.current = false;
        }

        setLogEntries(newLogEntries.slice(0, limit));
    }, [result]);

    return <Table columns={[
        {id: "date"},
        {id: "severity"},
        {id: "message"},
    ]} showHeader={false}>
        {logEntries.map(entry =>
            <Row key={entry.datetime} cells={{
                "date": <td className="td-shrink"><small><DateTime value={entry.datetime}/></small></td>,
                "severity": <td><small>{entry.severity}</small></td>,
                "message": <td><code>{formatJSON(entry.message)}</code></td>,
            }} />
        )}
    </Table>;
}

export function LogViewCard(props: WithAPI & AsRoute) {
    const [logSeverity, setLogSeverity] = useState<LogSeverity>("INFO");
    const [logLimit, setLogLimit] = useState<number>(20);

    const [tempLogLimit, setTempLogLimit] = useState<number>(logLimit);
    useTimeout(() => {
        if (tempLogLimit > 0) {
            setLogLimit(tempLogLimit);
        }
    }, 250, [tempLogLimit]);

    return <Card
        title={"Log"}
        header={
            <form class="form-inline">
                <label for="log-severity">Severity:</label>
                <select
                    name="severity"
                    id="log-severity"
                    value={logSeverity}
                    onInput={(e) => {
                        setLogSeverity(e.currentTarget.value as LogSeverity);
                    }}
                >
                    <option>DEBUG</option>
                    <option>INFO</option>
                    <option>WARNING</option>
                    <option>ERROR</option>
                    <option>CRITICAL</option>
                </select>

                <label for="log-limit">Number of messages:</label>
                <input
                    type="number"
                    name="limit"
                    id="log-limit"
                    value={tempLogLimit}
                    onInput={(e) => {
                        setTempLogLimit(Number.parseInt(e.currentTarget.value));
                    }}
                />
            </form>
        }
    >
        <LogView api={props.api} severity={logSeverity} limit={logLimit} />
    </Card>;
}
